import React, {useCallback, useState} from "react";
import { useForm } from "react-hook-form";
import {CellProps, Column} from "react-table";
import useAppRouter from "../../../../hooks/useAppRouter";
import {Table} from "../../common/table/Table";
import {Color, getColorText} from "../../../../enum/label/Color";
import {LabelFormModal, LabelFormValues} from "../Label/LabelFormModal";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {ApiClientError} from "../../../../apis/errors";
import useLoading from "../../../../hooks/useLoading";

interface Label {
  id: number | null;
  name: string;
  color: Color;
}

interface DataType extends Label {
  operation?: string
}

export interface LabelSetFormValues {
  name: string;
  content: string;
  labelList: DataType[];
}

interface LabelSetFormProps {
  defaultValues: LabelSetFormValues;
  isEdit: boolean;
  apiClientError: ApiClientError | null,
  clearApiClientError: () => Promise<void>;
  onSave: (data: LabelSetFormValues) => Promise<void>;
}

export const LabelSetForm = (props: LabelSetFormProps) => {
  const { defaultValues, isEdit, apiClientError, clearApiClientError, onSave } = props;
  const [ showLabelFormModal, setShowLabelFormModal ] = useState<boolean>(false);
  const [ isLabelFormEditMode, setIsLabelFormEditMode ] = useState<boolean>(false);
  const defaultNewLabelFormValues: LabelFormValues = {
    name: '',
    color: Color.RED,
  };
  const [ defaultLabelFormValues, setDefaultLabelFormValues ] = useState<LabelFormValues>(defaultNewLabelFormValues);
  const [ editLabelRowIndex, setEditLabelRowIndex ] = useState<number | null>(null);
  const { goto } = useAppRouter();
  const withLoading = useLoading();
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    reValidateMode: 'onSubmit',
  });

  const labelList = watch('labelList');

  const handleCreateLabelClick = async () => {
    setIsLabelFormEditMode(false);
    setEditLabelRowIndex(null);
    setDefaultLabelFormValues(defaultNewLabelFormValues);
    setShowLabelFormModal(true);
  }

  const handleAddLabel = async (label: LabelFormValues) => {
    setValue(
      'labelList',
      labelList.concat({
        id: null,
        name: label.name,
        color: label.color,
      })
    );
  }

  const handleSave = async (): Promise<void> => {
    await withLoading(async () => {
      await clearApiClientError();
      await handleSubmit(async (data: LabelSetFormValues) => {
        await onSave(data);
      })();
    });
  }

  const handleEditLabelClick = useCallback(async (rowIndex: number) => {
    setIsLabelFormEditMode(true);
    setEditLabelRowIndex(rowIndex);
    setDefaultLabelFormValues(labelList[rowIndex]);
    setShowLabelFormModal(true);
  }, [labelList]);

  const handleUpdateLabel = useCallback(async (label: LabelFormValues) => {
    setValue(
      'labelList',
      labelList.map((original, index) => {
         if (index === editLabelRowIndex) {
          return {
            id: original.id,
            name: label.name,
            color: label.color,
          };
        }

        return original;
      })
    );
  }, [labelList, editLabelRowIndex, setValue]);

  const handleDeleteLabelClick = useCallback(async (rowIndex: number) => {
    setValue(
      'labelList',
      labelList.filter((original, index) => {
        return (index !== rowIndex);
      })
    );
  }, [labelList, setValue]);

  const ColorCell = useCallback(({ value } : CellProps<DataType>) => {
    return (
      <>
        <span>{ getColorText(value) }</span>
      </>
    );
  }, []);

  const OperationCell = useCallback(({ row } : CellProps<DataType>) => {
    return (
      <>
        <button className="btn-primary w-20"
                type='button'
                onClick={() => {
                  handleEditLabelClick(row.index);
                }}
        >
          編集
        </button>
        {(row.original.id === null) &&
          (
            <button
              className="bg-gray-500 py-1 px-2 ml-1 text-white font-bold hover:opacity-80 rounded-full focus:outline-none"
              type='button'
              onClick={() => handleDeleteLabelClick(row.index)}
            >
              ×
            </button>
          )
        }
      </>
    );
  }, [handleEditLabelClick, handleDeleteLabelClick]);

  const columns: Column<DataType>[]  = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'name',
      },
      {
        Header: '色',
        accessor: 'color',
        Cell: ColorCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
      },
    ],
    [ColorCell, OperationCell]
  );

  return (
    <>
      {showLabelFormModal &&
        <LabelFormModal {...{
          isEdit: isLabelFormEditMode,
          defaultValues: defaultLabelFormValues,
          setShowModal: setShowLabelFormModal,
          onSave: isLabelFormEditMode ? handleUpdateLabel : handleAddLabel,
          onSaved: async () => { setShowLabelFormModal(false); },
        }}
        />
      }
      <form className="flex flex-col bg-sub min-h-full px-8">
        <div className="text-xl text-primary font-semibold py-6">ラベルセット{ isEdit ? '編集' : '作成' } </div>
        <div className="mb-4 text-danger text-xs">
          { apiClientError?.message }
          { (Object.keys(errors).length > 0) && '入力エラーがあります' }
        </div>

        { /* 名前 */ }
        <div className="mb-4">
          <label htmlFor="name">
            <div className="text-sm font-semibold text-primary mb-1">
              <span>名前</span>
              <span className="ml-2 required">必須</span>
            </div>
            <input
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register("name" ,
                makeRegisterOptions('名前', [
                  { type: FormValidationType.REQUIRED },
                  { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                ])
              )}
              id="name"
              className="text-field"
              type="text"
              maxLength={100}
            />
            <div className="mt-1 text-danger text-xs">
              {errors.name?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'name',
              }}
            />
          </label>
        </div>

        { /* 説明 */ }
        <div className="mb-4">
          <label htmlFor="content">
            <div className="text-sm font-semibold text-primary mb-1">
              説明
            </div>
            <textarea
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register('content',
                makeRegisterOptions('説明', [
                  { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                ])
              )}
              className="text-field"
              id="content"
              maxLength={255}
            />
            <div className="mt-1 text-danger text-xs">
              {errors.content?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'content',
              }}
            />
          </label>
        </div>


        <div className='flex items-center mb-1'>
          <div className="text-sm font-semibold text-primary">
            <span>ラベル</span>
            <span className="ml-2 required">必須</span>
          </div>
          <div className='ml-auto'>
            <button
              className="btn-primary ml-auto"
              type='button'
              onClick={() => {
                handleCreateLabelClick();
              }}
            >
              追 加
            </button>
          </div>
        </div>
        <Table
          tableData={labelList}
          columns={columns}
        />
        <input
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...register("labelList",
            makeRegisterOptions('ラベル', [
              { type: FormValidationType.REQUIRED },
            ])
          )}
          id="labelList"
          type="hidden"
          maxLength={100}
        />
        { /* errorsの対象が配列の場合、typeやmessageプロパティが存在しないとエラーになる。
           * 現状は「as any」等で回避してくれとの事。
           * https://github.com/react-hook-form/react-hook-form/issues/987
           */ }
        <div className="mt-1 text-danger text-xs">
          {(errors.labelList as any)?.message}
        </div>
        <ValidationErrorMessages
          {...{
            fieldErrors: apiClientError?.errors,
            field: 'labelList',
          }}
        />

        <div className="sticky flex bottom-0 bg-sub mt-auto py-4">
          <button
            className="btn-sub"
            type='button'
            onClick={() => goto('labelSetList')}
          >
            キャンセル
          </button>
          <button
            className="btn-primary ml-auto"
            type='button'
            onClick={handleSave}
          >
            保 存
          </button>
        </div>
      </form>
    </>
  );
}